// "Preview as signer" (Documents tab PR 9b, mockup v3 screen 6): the
// document as one signer will meet it, before anything is sent.
//
// Read only, and drawn entirely from what the field screen already has:
// the pages it rendered, the boxes placed on them, and what each bound
// box prints. Nothing is fetched, sent or saved. The chosen person's own
// boxes are what they will fill; everything the record or the sender
// fills is printed in; everybody else's boxes are named as theirs, the
// way the signing page shows a box that isn't yours to fill.

import { useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { Button, useFocusTrap } from "../../ui";
import type { PrepField, PrepStamp } from "./prepare-helpers";
import type { RenderedPdfPage } from "./render-pdf-pages";

/** The widest a page is drawn in the window, in CSS pixels. Narrower on
 *  a phone: the page scales to the window, and its boxes are placed in
 *  percentages of the page, so they scale with it. */
const PAGE_WIDTH = 640;

export interface PreviewSigner {
  key: string;
  name: string;
  color: string;
}

export interface PreviewAsSignerProps {
  documentName: string;
  pages: readonly RenderedPdfPage[];
  fields: readonly PrepField[];
  stamps: readonly PrepStamp[];
  /** In the order they sign; the first is who the window opens on. */
  signers: readonly PreviewSigner[];
  /** What a box prints when it goes out: a typed value, or what the
   *  record fills in. Null when the signer fills it themselves. */
  printed: (field: PrepField) => string | null;
  senderName: string;
  onClose: () => void;
}

/** What a signer's own box asks of them, in words. */
export function askFor(fieldType: string): string {
  switch (fieldType) {
    case "signature":
      return "Sign here";
    case "initials":
      return "Initial here";
    case "date_signed":
      return "Date signed (fills itself)";
    case "checkbox":
      return "Tick if it applies";
    default:
      return "Type here";
  }
}

function pictureOf(page: RenderedPdfPage): string | null {
  try {
    return page.canvas.toDataURL("image/png");
  } catch {
    return null;
  }
}

export function PreviewAsSigner(props: PreviewAsSignerProps) {
  const { documentName, pages, fields, stamps, signers, printed, senderName, onClose } = props;
  const [who, setWho] = useState<string | null>(signers[0]?.key ?? null);
  const sheetRef = useRef<HTMLDivElement | null>(null);
  useFocusTrap(true, sheetRef);
  const pictures = useMemo(() => new Map(pages.map((p) => [p.pageNumber, pictureOf(p)])), [pages]);
  const nameOf = (key: string) => signers.find((s) => s.key === key)?.name ?? "someone else";
  const colorOf = (key: string) => signers.find((s) => s.key === key)?.color ?? "var(--blue)";
  const chosen = signers.find((s) => s.key === who) ?? null;
  const mine = fields.filter((f) => f.signerKey === who && printed(f) === null).length;

  const tree = (
    <div className="overlay-backdrop esign-preview-backdrop" role="presentation">
      <div
        ref={sheetRef}
        className="esign-preview"
        role="dialog"
        aria-modal="true"
        aria-labelledby="esign-preview-title"
        onKeyDown={(e) => {
          if (e.key === "Escape") onClose();
        }}
      >
        <div className="esign-preview-head">
          <div>
            <h2 id="esign-preview-title" className="esign-preview-title">
              Preview as signer
            </h2>
            <p className="esign-preview-sub">
              {chosen
                ? `${documentName} as ${chosen.name} will see it: ${mine} ${mine === 1 ? "box" : "boxes"} to fill. Nothing has been sent.`
                : `${documentName}. Nothing has been sent.`}
            </p>
          </div>
          <div className="esign-preview-acts">
            {signers.length > 1 ? (
              <label className="esign-preview-who">
                <span>Seen by</span>
                <select value={who ?? ""} onChange={(e) => setWho(e.target.value)}>
                  {signers.map((s) => (
                    <option key={s.key} value={s.key}>
                      {s.name}
                    </option>
                  ))}
                </select>
              </label>
            ) : null}
            <Button variant="sw-default" onClick={onClose}>
              Close preview
            </Button>
          </div>
        </div>
        <div className="esign-preview-body">
          {pages.map((page) => {
            const src = pictures.get(page.pageNumber) ?? null;
            const box = (x: number, y: number, w: number, h: number) => ({
              left: `${(x / page.widthPt) * 100}%`,
              top: `${(y / page.heightPt) * 100}%`,
              width: `${(w / page.widthPt) * 100}%`,
              height: `${(h / page.heightPt) * 100}%`,
            });
            return (
              <div
                key={page.pageNumber}
                className="esign-preview-page"
                style={{ width: `min(${PAGE_WIDTH}px, 100%)`, aspectRatio: `${page.widthPt} / ${page.heightPt}` }}
                aria-label={`Page ${page.pageNumber}`}
              >
                {src ? <img src={src} alt="" /> : null}
                {fields
                  .filter((f) => f.pageNumber === page.pageNumber)
                  .map((f) => {
                    // A strike (PR 4a) shows as the line it will be on the page.
                    if (f.strike) {
                      return (
                        <span key={f.key} className="esign-preview-box" data-kind="strike" style={box(f.x, f.y, f.width, f.height)} />
                      );
                    }
                    const text = printed(f);
                    if (text !== null) {
                      return (
                        <span key={f.key} className="esign-preview-box" data-kind="printed" style={box(f.x, f.y, f.width, f.height)}>
                          {text}
                        </span>
                      );
                    }
                    const isMine = f.signerKey === who;
                    return (
                      <span
                        key={f.key}
                        className="esign-preview-box"
                        data-kind={isMine ? "mine" : "theirs"}
                        style={{ ...box(f.x, f.y, f.width, f.height), borderColor: colorOf(f.signerKey) }}
                      >
                        {isMine ? askFor(f.fieldType) : `For ${nameOf(f.signerKey)}`}
                      </span>
                    );
                  })}
                {stamps
                  .filter((s) => s.pageNumber === page.pageNumber)
                  .map((s) => (
                    <span key={s.key} className="esign-preview-box" data-kind="printed" style={box(s.x, s.y, s.width, s.height)}>
                      {senderName} · signed before it goes
                    </span>
                  ))}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
  if (typeof document === "undefined") return tree;
  return createPortal(tree, document.body);
}
